import type { BuyerSubmissionStatus } from "@/api/buyers";

/**
 * The rules for a COMBINED intro email — one email to one buyer introducing two
 * or more of their listings — stated once on the client.
 *
 * <p>The server twin is `PhoenixCapital.Core/Common/BuyerCombinedIntroRules.cs`,
 * and it is the one that enforces them: it refuses a draft, send or copy that
 * breaks any of these. Nothing imports across the language boundary, so the two
 * are held together by the shared fixture
 * `tests/fixtures/buyer-combined-intro-rules.json`, read by
 * `buyerCombinedIntroRules.test.ts` here and
 * `BuyerCombinedIntroRulesParityTests.cs` there. That is what stops the table
 * offering a checkbox the server then refuses.</p>
 */

/** The fewest listings a combined intro covers. One is a single intro. */
export const COMBINED_INTRO_MIN_LISTINGS = 2;

/** The most listings one combined intro covers. */
export const COMBINED_INTRO_MAX_LISTINGS = 50;

/**
 * At or below this, each listing gets its own paragraph; above it, one line
 * each. A constant, not a setting, because the composer's explainer states the
 * number in words.
 */
export const COMBINED_INTRO_FULL_MODE_MAX_LISTINGS = 5;

/** The whole email as the buyer receives it — the communications log's cap. */
export const COMBINED_INTRO_MAX_EMAIL_CHARACTERS = 50_000;

/** The longest AI paragraph accepted for one listing. */
export const COMBINED_INTRO_MAX_PARAGRAPH_WORDS = 160;

/**
 * The statuses that cannot go in a combined intro (ruling D5), with the label
 * the table shows beside a disabled checkbox. Each label equals `buyerLabel`'s
 * rendering of that status, which the shared fixture pins.
 */
export const COMBINED_INTRO_BLOCKED_STATUSES: Readonly<
  Partial<Record<BuyerSubmissionStatus, string>>
> = {
  DeclinedByBuyer: "Declined by buyer",
  RejectedByBroker: "Rejected by broker",
  Passed: "Passed on",
  Dead: "Dead",
  ClosedPurchased: "Closed — purchased",
  EscalatedToDeal: "Escalated to deal",
  NoResponse: "No response",
};

/** The label for a status that cannot go in a combined intro, else null. */
export function combinedIntroBlockedLabel(status: BuyerSubmissionStatus): string | null {
  return COMBINED_INTRO_BLOCKED_STATUSES[status] ?? null;
}

/** D4: an archived submission is left out of any combined intro, whatever its
 *  status ("Left out — archived"). Server twin `BuyerCombinedIntroRules.ArchivedLabel`. */
export const COMBINED_INTRO_ARCHIVED_LABEL = "archived";

/** Why a row can't go in a combined intro — archived first, then a blocked
 *  status — or null when it can. Server twin `BlockedLabel(status, isArchived)`. */
export function combinedIntroLeftOutReason(row: {
  status: BuyerSubmissionStatus;
  isArchived?: boolean;
}): string | null {
  return row.isArchived ? COMBINED_INTRO_ARCHIVED_LABEL : combinedIntroBlockedLabel(row.status);
}

export type CombinedIntroMode = "Full" | "Short";

/** Which mode a combined intro of this many listings is drafted in. */
export function combinedIntroModeFor(listingCount: number): CombinedIntroMode {
  return listingCount <= COMBINED_INTRO_FULL_MODE_MAX_LISTINGS ? "Full" : "Short";
}
